<template>
  <div class="tour-overlay" :class="{ 'is-exiting': exiting, 'no-background': step === 2 || step === 3 }">
    <div class="aurora-backdrop" :class="{ 'is-active': step === 1 || step === 5 }"></div>

    <div class="tour-backdrop" :style="backdropStyle"></div>

    <div v-if="step === 1" class="tour-step welcome-step">
      <v-img src="/img/roody_logo.png" class="welcome-logo" contain></v-img>

      <h1 class="welcome-title">
        <span class="word mr-2">Dein </span>
        <span class="word">Trainingshub</span>
      </h1>
      <p class="welcome-subtitle">Der zentrale Ort, um deine Pläne zu starten, Fortschritte zu tracken und Ziele zu erreichen.</p>

      <v-btn x-large depressed color="white" class="font-weight-bold black--text mt-8 pulse-glow-btn" @click="nextStep">
        Entdecken <v-icon right>mdi-arrow-right</v-icon>
      </v-btn>
      <v-btn depressed color="white" class="font-weight-bold black--text mt-8" @click="$router.push('/')"> zurück </v-btn>
    </div>
    <div v-if="step === 2">
      <div v-if="hasWorkoutPlan" class="tour-step tour-tooltip" :style="tooltipStyle">
        <div class="tooltip-content">
          <p>Starte hier direkt dein nächstes Workout! 🚀</p>
          <v-btn small outlined color="white" @click="nextStep">Weiter</v-btn>
        </div>
      </div>
      <div v-else class="tour-step create-plan-step">
        <div class="emoji-line">🚀</div>
        <h2 class="create-plan-title">
          <span class="line1">Bereit,</span><br />
          <span class="line2">durchzustarten?</span>
        </h2>
        <p class="create-plan-subtitle">
          <span v-if="hasOldPlans">
            Deine alten Pläne sind bereit für ein Upgrade! Wandle sie um oder starte ein <strong>freies Training</strong>, um deine Reise zu beginnen.
          </span>
          <span v-else>
            Hier beginnt deine Reise. Da du noch keinen festen Plan hast, ist ein <strong>freies Training</strong> der perfekte Start. Gestalte dein
            Workout spontan und entdecke, was in dir steckt.
          </span>
        </p>

        <v-btn v-if="hasOldPlans" x-large rounded depressed color="primary" class="mt-4 font-weight-bold shine-btn" @click="startConversion">
          Pläne umwandeln <v-icon right small>mdi-autorenew</v-icon>
        </v-btn>
        <v-btn rounded depressed color="white" class="font-weight-bold black--text mt-3 create-plan-btn" @click="createPlan">
          <v-icon left>mdi-plus-circle</v-icon> Freies Training starten
        </v-btn>
      </div>
    </div>

    <div v-if="step > 2 && step < 5 && tooltipStyle.display" class="tour-step tour-tooltip" :style="tooltipStyle">
      <div class="tooltip-content">
        <p>{{ tooltipText }}</p>
        <v-btn small outlined color="white" @click="nextStep">{{ isLastStep ? "Abschliessen" : "Weiter" }}</v-btn>
      </div>
    </div>

    <div v-if="step === 5" class="tour-step welcome-step final-step">
      <div class="final-celebration">🎉</div>
      <h1 class="welcome-title">Viel Erfolg!</h1>
      <p class="welcome-subtitle">Let's get stronger.</p>
    </div>
  </div>
</template>
<script>
export default {
  name: "HubTour",
  props: {
    hasWorkoutPlan: {
      type: Boolean,
      required: true,
    },
    hasOldPlans: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      step: 1,
      exiting: false,
      backdropStyle: {},
      tooltipStyle: { display: "none" },
    }
  },
  computed: {
    isLastStep() {
      return !this.hasWorkoutPlan ? this.step === 2 : this.step === 4
    },
    tooltipText() {
      const texts = {
        3: "Wechsle die Ansicht zwischen Trainings & Übungen. 🏋️‍♂️",
        4: "Swipe durch deine Erfolge und sieh dir Details an! ✨",
      }
      return texts[this.step] || ""
    },
  },
  methods: {
    createPlan() {
      this.exiting = true

      this.$emit("create-plan")
    },
    startConversion() {
      this.exiting = true

      this.$emit("start-conversion")
    },
    async nextStep() {
      if (this.isLastStep) {
        this.step = 5
        await this.updateSpotlight()
        setTimeout(() => this.closeTour(), 2000)
      } else {
        this.step++
        await this.$nextTick()
        await this.updateSpotlight()
      }
    },
    async getTargetBoundingBox(selector, retries = 5) {
      for (let i = 0; i < retries; i++) {
        const element = document.querySelector(selector)
        if (element) return element.getBoundingClientRect()
        await new Promise((resolve) => setTimeout(resolve, 100))
      }
      console.warn(`Tour target not found: ${selector}`)
      return null
    },
    async updateSpotlight() {
      this.tooltipStyle = { display: "none" }

      const targets = {
        2: {
          selector: "#tour-step-2-target",
          tooltipAnchorSelector: "#tour-step-2-headline",
          padding: 15,
          radiusFactor: 1.2,
        },
        3: {
          selector: "#tour-step-3-target",
          padding: 10,
          radiusFactor: 1,
        },
        4: {
          selector: "#tour-step-4-target",
          tooltipAnchorSelector: "#tour-step-3-target",
          padding: 15,
          radiusFactor: 1.1,
        },
      }

      const targetInfo = targets[this.step]

      if (!targetInfo || (this.step === 2 && !this.hasWorkoutPlan)) {
        this.backdropStyle.maskImage = "none"
        if (this.step === 2 && this.hasWorkoutPlan) {
          const tooltipAnchorRect = await this.getTargetBoundingBox("#tour-step-2-headline")
          if (tooltipAnchorRect) {
            this.tooltipStyle = {
              display: "block",
              top: `${tooltipAnchorRect.bottom + 10}px`,
            }
          }
        }
        return
      }

      const spotlightRect = await this.getTargetBoundingBox(targetInfo.selector)
      if (!spotlightRect) {
        return this.nextStep()
      }

      const spotlightX = spotlightRect.left + spotlightRect.width / 2
      const spotlightY = spotlightRect.top + spotlightRect.height / 2
      const spotlightRadius = (Math.max(spotlightRect.width, spotlightRect.height) / 2 + targetInfo.padding) * (targetInfo.radiusFactor || 1)
      this.backdropStyle.maskImage = `radial-gradient(circle at ${spotlightX}px ${spotlightY}px, transparent ${
        spotlightRadius * 0.9
      }px, black ${spotlightRadius}px)`

      const tooltipAnchorSelector = targetInfo.tooltipAnchorSelector || targetInfo.selector
      const tooltipAnchorRect = await this.getTargetBoundingBox(tooltipAnchorSelector)

      if (tooltipAnchorRect) {
        this.tooltipStyle = {
          display: "block",
          top: `${tooltipAnchorRect.bottom + 10}px`,
        }
      }
    },
    closeTour() {
      this.exiting = true
      setTimeout(() => {
        this.$emit("complete")
      }, 500)
    },
    handleResize() {
      if (this.step > 1 && this.step < 5) {
        this.updateSpotlight()
      }
    },
  },
  mounted() {
    window.addEventListener("resize", this.handleResize)
    // KORRIGIERTE LOGIK: Die Tour startet jetzt immer bei Schritt 1.
    // Der Benutzer klickt auf "Tour starten", um zu Schritt 2 zu gelangen,
    // wo dann die korrekte Ansicht (je nachdem ob Pläne da sind oder nicht) angezeigt wird.
    this.updateSpotlight()
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize)
  },
}
</script>
<style scoped>
/* --- GRUNDEINSTELLUNGEN & OVERLAY --- */
.tour-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  animation: fade-in 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  background: #000;
}
.no-background {
  background: transparent !important;
}
.tour-overlay.is-exiting {
  animation: fade-out 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}
.tour-step {
  position: absolute;
  left: 0;
  right: 0;
  margin: 0 auto;
  color: white;
  text-align: center;
  z-index: 10002; /* Über den Backdrops */
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* --- HINTERGRUND-EFFEKTE --- */
.aurora-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(ellipse at 70% 30%, rgba(0, 115, 255, 0.2), transparent 50%),
    radial-gradient(ellipse at 30% 70%, rgba(0, 163, 150, 0.4), transparent 50%);
  animation: aurora-pan 20s linear infinite;
  opacity: 0;
  transition: opacity 1.5s ease;
  z-index: 10000;
}
.aurora-backdrop.is-active {
  opacity: 1;
}
.tour-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(5px);
  transition: all 0.9s cubic-bezier(0.45, 0, 0.1, 1);
  -webkit-mask-size: 400vmax;
  mask-size: 400vmax;
  -webkit-mask-position: center;
  mask-position: center;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  z-index: 10001;
}

/* --- TOOLTIP --- */
.tour-tooltip {
  animation: fade-in-up 0.6s cubic-bezier(0.19, 1, 0.22, 1) forwards;
  opacity: 0;
  transition: top 0.9s cubic-bezier(0.45, 0, 0.1, 1);
}
.tooltip-content {
  background: rgba(25, 25, 25, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  padding: 18px 26px;
  border-radius: 18px;
  max-width: 320px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5), inset 0 0 0 1px rgba(255, 255, 255, 0.05);
}
.tooltip-content p {
  margin: 0 0 16px 0;
  font-size: 1rem;
  line-height: 1.5;
  text-shadow: 0 1px 5px rgba(0, 0, 0, 0.5);
}
.tooltip-content .v-btn {
  transition: all 0.3s ease;
}
.tooltip-content .v-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: scale(1.05);
}

/* --- WILLKOMMEN & ABSCHLUSS (ANGEPASST) --- */
.welcome-step {
  top: 50%;
  transform: translateY(-50%);
  height: 100%;
}
.welcome-logo {
  opacity: 0;
  transform: scale(0.8);
  max-height: 25vh !important;
  animation: logo-pop-in 1s 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  margin-bottom: 24px;
}
.welcome-title {
  font-size: 4rem;
  font-weight: 800;
  letter-spacing: -1.5px;
  line-height: 1.1;
  text-shadow: 0 0 50px rgba(255, 255, 255, 0.5);
}
.welcome-title .word {
  display: inline-block;
  opacity: 0;
  transform: translateY(40px);
  animation: reveal-text-glow 1s cubic-bezier(0.19, 1, 0.22, 1) forwards;
}
.welcome-title .word:nth-child(1) {
  animation-delay: 0.6s;
}
.welcome-title .word:nth-child(2) {
  animation-delay: 0.8s;
}
.welcome-subtitle {
  font-size: 1.2rem;
  max-width: 400px;
  line-height: 1.6;
  opacity: 0;
  margin-top: 16px;
  animation: fade-in-up 0.8s 1s forwards;
}
.final-step .welcome-title,
.final-step .welcome-subtitle {
  animation-delay: 0s;
}
.final-celebration {
  font-size: 5rem;
  animation: celebration-pop 1.5s cubic-bezier(0.1, 1.2, 0.3, 1) forwards;
  opacity: 0;
}

/* --- CREATE PLAN SCREEN --- */
.create-plan-step {
  top: 50%;
  transform: translateY(-50%);
  height: 100%;
}
.emoji-line {
  font-size: 4rem;
  animation: pop-in 0.6s 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  opacity: 0;
}
.create-plan-title {
  font-size: 3.2rem;
  font-weight: 800;
  line-height: 1.1;
  margin: 16px 0;
  text-shadow: 0 0 30px rgba(255, 255, 255, 0.3);
}
.create-plan-title span {
  display: inline-block;
  opacity: 0;
  transform: translateY(30px);
  animation: reveal-text 0.8s cubic-bezier(0.19, 1, 0.22, 1) forwards;
}
.create-plan-title .line1 {
  animation-delay: 0.4s;
}
.create-plan-title .line2 {
  animation-delay: 0.6s;
}
.create-plan-subtitle {
  font-size: 1.1rem;
  max-width: 380px;
  opacity: 0;
  transform: translateY(20px);
  animation: reveal-text 0.8s 0.8s cubic-bezier(0.19, 1, 0.22, 1) forwards;
}

/* --- BUTTON-EFFEKTE --- */
.pulse-glow-btn {
  border-radius: 16px !important;
  animation: pulse-glow 2.5s infinite;
  transition: transform 0.3s ease;
  opacity: 0;
  animation-delay: 1.2s; /* Startet nach den Texten */
  animation-fill-mode: forwards;
}
.pulse-glow-btn:hover {
  transform: scale(1.05);
  animation-play-state: paused;
}
.shine-btn {
  position: relative;
  overflow: hidden;
}
.shine-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 75%;
  height: 100%;
  background: linear-gradient(100deg, rgba(255, 255, 255, 0) 20%, rgba(255, 255, 255, 0.3) 50%, rgba(255, 255, 255, 0) 80%);
  transform: skewX(-25deg);
  animation: shine-effect 3s infinite linear;
  animation-delay: 1.5s;
}

/* --- KEYFRAME ANIMATIONEN --- */
@keyframes fade-in {
  to {
    opacity: 1;
  }
}
@keyframes fade-out {
  to {
    opacity: 0;
  }
}
@keyframes pop-in {
  to {
    opacity: 1;
    transform: scale(1);
  }
}
@keyframes logo-pop-in {
  to {
    opacity: 1;
    transform: scale(1);
  }
}
@keyframes reveal-text {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes reveal-text-glow {
  to {
    opacity: 1;
    transform: translateY(0);
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.5), 0 0 50px rgba(255, 255, 255, 0.3);
  }
}
@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes aurora-pan {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
@keyframes pulse-glow {
  0% {
    opacity: 1;
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.3), 0 0 20px rgba(255, 255, 255, 0.2);
  }
  50% {
    opacity: 1;
    box-shadow: 0 0 25px rgba(255, 255, 255, 0.6), 0 0 40px rgba(255, 255, 255, 0.4);
  }
  100% {
    opacity: 1;
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.3), 0 0 20px rgba(255, 255, 255, 0.2);
  }
}
@keyframes shine-effect {
  to {
    left: 125%;
  }
}
@keyframes celebration-pop {
  0% {
    opacity: 0;
    transform: scale(0.5) rotate(0deg);
  }
  50% {
    opacity: 1;
    transform: scale(1.2) rotate(-10deg);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
}

/* --- RESPONSIVE ANPASSUNGEN --- */
@media (max-width: 600px) {
  .welcome-title {
    font-size: 3rem;
  }
  .create-plan-title {
    font-size: 2.5rem;
  }
}
</style>
